import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { suggestTool } from "../api/api";
import { useAuth } from '../utils/AuthContext';
import StickyNavbar from "../components/StickyNavbar";
import CSpinner from '../components/CSpinner';
import { motion } from "framer-motion";

const SuggestTool = () => {
  const { token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [userRole, setUserRole] = useState('');
  
  // Form data state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    website: '',
    category: '',
    features: '',
    pricing: 'Free',
    reason: '',
    contactEmail: ''
  });

  // Form validation state
  const [formErrors, setFormErrors] = useState({
    name: '',
    description: '',
    website: '',
    category: '',
    features: '',
    reason: '',
    contactEmail: ''
  });
  
  // Form touched state to track which fields user has interacted with
  const [touched, setTouched] = useState({
    name: false,
    description: false,
    website: false,
    category: false,
    features: false,
    reason: false,
    contactEmail: false
  });

  // Define required fields and validation rules based on user role
  const getRequiredFields = () => {
    if (userRole === 'user') {
      return ['name', 'description', 'website', 'reason'];
    } else if (userRole === 'owner') {
      return ['name', 'description', 'website', 'category', 'features', 'reason', 'contactEmail'];
    }
    return [];
  };

  // Validate individual form field with role-specific logic
  const validateField = (name, value) => {
    const requiredFields = getRequiredFields();
    let error = '';
    
    // Skip validation for fields not required by current role
    if (!requiredFields.includes(name) && name !== 'pricing') {
      return '';
    }
    
    switch (name) {
      case 'name':
        if (!value.trim()) {
          error = 'Tool name is required';
        } else if (value.trim().length < 2) {
          error = 'Tool name must be at least 2 characters';
        } else if (value.trim().length > 50) {
          error = 'Tool name must be less than 50 characters';
        }
        break;
        
      case 'description':
        if (!value.trim()) {
          error = 'Description is required';
        } else if (value.trim().length < 10) {
          error = 'Please provide a more detailed description (at least 10 characters)';
        } else if (value.trim().length > 500) {
          error = 'Description must be less than 500 characters';
        }
        break;
        
      case 'website':
        if (!value.trim()) {
          error = 'Website URL is required';
        } else {
          try {
            let urlToTest = value;
            if (!/^https?:\/\//i.test(value)) {
              urlToTest = 'https://' + value;
            }

            const url = new URL(urlToTest);
            if (!['http:', 'https:'].includes(url.protocol)) {
              error = 'URL must start with http:// or https://';
            }
          } catch (e) {
            error = 'Please enter a valid URL (e.g., https://example.com)';
          }
        }
        break;
        
      case 'category':
        // Only required for owners
        if (userRole === 'owner') {
          if (!value.trim()) {
            error = 'Category is required';
          } else if (value.trim().length > 100) {
            error = 'Category must be less than 100 characters';
          }
        }
        break;
        
      case 'features':
        // Only required for owners
        if (userRole === 'owner') {
          if (!value.trim()) {
            error = 'Key features are required';
          } else if (value.trim().length > 300) {
            error = 'Features must be less than 300 characters';
          }
        }
        break;
        
      case 'reason':
        // Different validation based on user role
        if (!value.trim()) {
          error = userRole === 'user' 
            ? 'Please explain why you recommend this tool' 
            : 'Please explain why your tool should be added';
        } else if (value.trim().length < 20) {
          error = 'Please provide a more detailed reason (at least 20 characters)';
        } else if (value.trim().length > 500) {
          error = 'Reason must be less than 500 characters';
        }
        break;
        
      case 'contactEmail':
        // Only required for owners
        if (userRole === 'owner') {
          if (!value.trim()) {
            error = 'Contact email is required';
          } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) {
            error = 'Please enter a valid email address';
          }
        }
        break;
        
      default:
        break;
    }
    
    return error;
  };

  // Validate all form fields based on role
  const validateForm = () => {
    let isValid = true;
    const errors = {};
    const requiredFields = getRequiredFields();
    
    // Only validate required fields for the current role
    requiredFields.forEach(field => {
      const error = validateField(field, formData[field]);
      errors[field] = error;
      
      if (error) {
        isValid = false;
      }
    });
    
    setFormErrors(prev => ({
      ...prev,
      ...errors
    }));
    
    return isValid;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Real-time validation only for touched fields
    if (touched[name]) {
      const error = validateField(name, value);
      setFormErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    
    // Mark field as touched
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
    
    // Validate on blur
    const error = validateField(name, formData[name]);
    setFormErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const handleRoleSelection = (role) => {
    setUserRole(role);
    
    // Reset errors and touched state when changing roles
    setFormErrors({
      name: '',
      description: '',
      website: '',
      category: '',
      features: '',
      reason: '',
      contactEmail: ''
    });
    
    setTouched({
      name: false,
      description: false,
      website: false,
      category: false,
      features: false,
      reason: false,
      contactEmail: false
    });
    
    // Pre-fill contactEmail for owner if user is authenticated
    if (role === 'owner' && isAuthenticated) {
      // This would typically come from the user's profile
      // For demo purposes, we're not implementing this fully
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Mark required fields as touched to show validation errors
    const touchedFields = {};
    const requiredFields = getRequiredFields();
    requiredFields.forEach(field => {
      touchedFields[field] = true;
    });
    
    setTouched(prev => ({
      ...prev,
      ...touchedFields
    }));
    
    // Validate form before submission
    if (!validateForm()) {
      // Scroll to the first error
      const firstErrorField = document.querySelector('.error-message');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Include role in the submission
      const dataToSubmit = {
        ...formData,
        userType: userRole
      };

      if (dataToSubmit.features) {
        dataToSubmit.features = dataToSubmit.features.split(',').map(item => item.trim());
      }
      
      // For user role, only send relevant fields
      if (userRole === 'user') {
        // Filter out fields not needed for user role
        const { category, features, contactEmail, ...userFields } = dataToSubmit;
        //alert(JSON.stringify(userFields));
        await suggestTool(userFields, token);
      } else {
        // Send all fields for owner role
        await suggestTool(dataToSubmit, token);
      }
      
      setSuccess(true);
      
      // Reset form after successful submission
      setFormData({
        name: '',
        description: '',
        website: '',
        category: '',
        features: '',
        pricing: 'Free',
        reason: '',
        contactEmail: ''
      });
      
    } catch (err) {
      console.error('Error submitting tool suggestion:', err);
      setError(err.message || 'Failed to submit suggestion');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setUserRole('');
    setSuccess(false);
    setError(null);
    setFormErrors({
      name: '',
      description: '',
      website: '',
      category: '',
      features: '',
      reason: '',
      contactEmail: ''
    });
    setTouched({
      name: false,
      description: false,
      website: false,
      category: false,
      features: false,
      reason: false,
      contactEmail: false
    });
  };

  // Helper function to check if a field is required based on user role
  const isFieldRequired = (fieldName) => {
    return getRequiredFields().includes(fieldName);
  };

  // Helper function to show validation state
  const getInputClassName = (fieldName) => {
    const baseClass = "w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500";
    
    // Only show validation state for required fields
    if (isFieldRequired(fieldName) && touched[fieldName]) {
      if (formErrors[fieldName]) {
        return `${baseClass} border-red-500 bg-red-50`;
      }
      return `${baseClass} border-green-500 bg-green-50`;
    }
    
    return `${baseClass} border-gray-300`;
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 260,
        damping: 20
      }
    }
  };

  // Displays a required asterisk if the field is required for the current role
  const RequiredIndicator = ({ fieldName }) => {
    if (isFieldRequired(fieldName)) {
      return <span className="text-red-500">*</span>;
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <StickyNavbar />
      
      <div className="flex-1 flex flex-col items-center px-6 pt-20">
        <motion.div 
          className="w-full max-w-2xl mb-16"
          variants={containerVariants}
          initial="hidden"
          animate="visible">
          <motion.div 
            className="bg-white rounded-xl shadow-lg overflow-hidden"
            variants={itemVariants}>
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4">
              <h1 className="text-xl font-bold text-white">Suggest a Tool</h1>
              <p className="text-blue-100 mt-1">Help us grow our AI tools database</p>
            </div>
            
            {/* Content */}
            <div className="p-6">
              {success ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                  </div>
                  <h3 className="text-xl font-medium text-gray-900 mb-2">Thank You!</h3>
                  <p className="text-gray-600 mb-6">
                    Your tool suggestion has been submitted successfully. Our team will review it shortly.
                  </p>
                  <button
                    onClick={resetForm}
                    className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Suggest Another Tool
                  </button>
                </div>
              ) : !userRole ? (
                <div className="py-4">
                  <h2 className="text-lg font-semibold text-gray-800 mb-4">What's your relationship with this tool?</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                    <motion.div
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.98 }}
                      className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer overflow-hidden"
                      onClick={() => handleRoleSelection('user')}
                    >
                      <div className="p-5">
                        <div className="flex justify-center mb-4">
                          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                            </svg>
                          </div>
                        </div>
                        <h3 className="text-lg font-semibold text-center text-gray-800 mb-2">I'm a User</h3>
                        <p className="text-gray-600 text-center text-sm">
                          I've used this tool and would like to recommend it to others
                        </p>
                      </div>
                    </motion.div>
                    
                    <motion.div
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.98 }}
                      className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer overflow-hidden"
                      onClick={() => handleRoleSelection('owner')}
                    >
                      <div className="p-5">
                        <div className="flex justify-center mb-4">
                          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center">
                            <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                            </svg>
                          </div>
                        </div>
                        <h3 className="text-lg font-semibold text-center text-gray-800 mb-2">I'm the Owner/Developer</h3>
                        <p className="text-gray-600 text-center text-sm">
                          I'm part of the team that created or maintains this tool
                        </p>
                      </div>
                    </motion.div>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="py-4">
                  <h2 className="text-lg font-semibold text-gray-800 mb-6">
                    {userRole === 'user' ? 'Recommend a Tool' : 'Submit Your Tool'}
                  </h2>
                  
                  {/* Role indicator */}
                  <div className="mb-6 flex items-center">
                    <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                      userRole === 'user' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                    }`}>
                      {userRole === 'user' ? 'Recommending as a User' : 'Submitting as an Owner/Developer'}
                    </div>
                    <button 
                      type="button"
                      onClick={() => setUserRole('')}
                      className="ml-2 text-sm text-gray-600 hover:text-gray-800 underline"
                    >
                      Change
                    </button>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                        Tool Name <RequiredIndicator fieldName="name" />
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required={isFieldRequired('name')}
                        placeholder="e.g. ChatGPT"
                        value={formData.name}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        className={getInputClassName('name')}
                      />
                      {touched.name && formErrors.name && (
                        <p className="mt-1 text-sm text-red-600 error-message">{formErrors.name}</p>
                      )}
                    </div>
                    
                    <div>
                      <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1">
                        Website URL <RequiredIndicator fieldName="website" />
                      </label>
                      <input
                        type="url"
                        id="website"
                        name="website"
                        required={isFieldRequired('website')}
                        placeholder="https://example.com"
                        value={formData.website}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        className={getInputClassName('website')}
                      />
                      {touched.website && formErrors.website && (
                        <p className="mt-1 text-sm text-red-600 error-message">{formErrors.website}</p>
                      )}
                    </div>
                    
                    <div>
                      <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                        Brief Description <RequiredIndicator fieldName="description" />
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        required={isFieldRequired('description')}
                        placeholder={userRole === 'user' 
                          ? "What does this tool do? Why is it useful?" 
                          : "Describe your tool's purpose and primary use cases"}
                        value={formData.description}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        rows="3"
                        className={getInputClassName('description')}
                      />
                      {touched.description && formErrors.description && (
                        <p className="mt-1 text-sm text-red-600 error-message">{formErrors.description}</p>
                      )}
                      {touched.description && !formErrors.description && (
                        <p className="mt-1 text-sm text-green-600">
                          {500 - formData.description.length} characters remaining
                        </p>
                      )}
                    </div>
                    
                    {/* Only show owner-specific fields if role is 'owner' */}
                    {userRole === 'owner' && (
                      <>
                        <div>
                          <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                            Category <RequiredIndicator fieldName="category" />
                          </label>
                          <input
                            type="text"
                            id="category"
                            name="category"
                            required={isFieldRequired('category')}
                            placeholder="e.g. Text Generation, Image Editing"
                            value={formData.category}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                            className={getInputClassName('category')}
                          />
                          {touched.category && formErrors.category && (
                            <p className="mt-1 text-sm text-red-600 error-message">{formErrors.category}</p>
                          )}
                        </div>
                        
                        <div>
                          <label htmlFor="features" className="block text-sm font-medium text-gray-700 mb-1">
                            Key Features <RequiredIndicator fieldName="features" />
                          </label>
                          <textarea
                            id="features"
                            name="features"
                            required={isFieldRequired('features')}
                            placeholder="List the main features (comma separated)"
                            value={formData.features}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                            rows="2"
                            className={getInputClassName('features')}
                          />
                          {touched.features && formErrors.features && (
                            <p className="mt-1 text-sm text-red-600 error-message">{formErrors.features}</p>
                          )}
                          {touched.features && !formErrors.features && (
                            <p className="mt-1 text-sm text-green-600">
                              {300 - formData.features.length} characters remaining
                            </p>
                          )}
                        </div>
                        
                        <div>
                          <label htmlFor="pricing" className="block text-sm font-medium text-gray-700 mb-1">
                            Pricing Model <RequiredIndicator fieldName="pricing" />
                          </label>
                          <select
                            id="pricing"
                            name="pricing"
                            required={isFieldRequired('pricing')}
                            value={formData.pricing}
                            onChange={handleInputChange}
                            className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="Free">Free</option>
                            <option value="Freemium">Freemium</option>
                            <option value="Paid">Paid</option>
                            <option value="Subscription">Subscription</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>
                        
                        <div>
                          <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-700 mb-1">
                            Contact Email <RequiredIndicator fieldName="contactEmail" />
                          </label>
                          <input
                            type="email"
                            id="contactEmail"
                            name="contactEmail"
                            required={isFieldRequired('contactEmail')}
                            placeholder="For verification purposes"
                            value={formData.contactEmail}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                            className={getInputClassName('contactEmail')}
                          />
                          {touched.contactEmail && formErrors.contactEmail && (
                            <p className="mt-1 text-sm text-red-600 error-message">{formErrors.contactEmail}</p>
                          )}
                        </div>
                      </>
                    )}
                    
                    <div>
                      <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-1">
                        {userRole === 'user' 
                          ? 'Why do you recommend this tool?' 
                          : 'Why should we add your tool?'} 
                        <RequiredIndicator fieldName="reason" />
                      </label>
                      <textarea
                        id="reason"
                        name="reason"
                        required={isFieldRequired('reason')}
                        placeholder={userRole === 'user' 
                          ? "Tell us why you find this tool valuable" 
                          : "Tell us why your tool would be valuable for our users"}
                        value={formData.reason}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        rows="3"
                        className={getInputClassName('reason')}
                      />
                      {touched.reason && formErrors.reason && (
                        <p className="mt-1 text-sm text-red-600 error-message">{formErrors.reason}</p>
                      )}
                      {touched.reason && !formErrors.reason && (
                        <p className="mt-1 text-sm text-green-600">
                          {500 - formData.reason.length} characters remaining
                        </p>
                      )}
                    </div>
                    
                    {error && (
                      <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                        {error}
                      </div>
                    )}
                  </div>
                  
                  {/* Form helper text */}
                  <div className="mt-4 text-sm text-gray-500">
                    <p>
                      <span className="text-red-500">*</span> indicates required fields based on your selected role
                    </p>
                  </div>
                  
                  <div className="mt-6 flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => setUserRole('')}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors">
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center">
                      {loading ? (
                        <>
                          <CSpinner className="w-4 h-4 mr-2" />
                          Submitting...
                        </>
                      ) : "Submit Suggestion"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
          
          {/* Additional information card */}
          <motion.div 
            className="bg-white rounded-xl shadow-md mt-6 p-5 border-l-4 border-blue-500"
            variants={itemVariants}
          >
            <h3 className="text-md font-semibold text-gray-800 mb-2">How we review submissions</h3>
            <p className="text-sm text-gray-600">
              Our team reviews all tool suggestions within 1-2 business days. We check for relevance, functionality, and value to our user community. You'll receive a notification when your suggested tool is approved and added to our database.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default SuggestTool;